import { useCallback, useEffect, useRef, useState } from 'react';
import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import type { Process } from '../type/Process';

interface UseProcessSelectionResult {
  processes: Process[];
  isLoading: boolean;
  error: string | null;
  selectedProcess: Process | null;
  skipped: boolean;
  select: (p: Process) => void;
  skip: () => void;   // "Start blank" or "Clear" — hides picker, shows full form
  reopen: () => void; // "Change" — clears selection, re-shows picker
}

export function useProcessSelection(
  apiClient: TraceabilityApiClient | null,
  toolReference: string | null
): UseProcessSelectionResult {
  const [processes, setProcesses] = useState<Process[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [skipped, setSkipped] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!apiClient || !toolReference) {
      setProcesses([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    abortControllerRef.current?.abort();
    const ac = new AbortController();
    abortControllerRef.current = ac;

    setIsLoading(true);
    setError(null);

    apiClient
      .processReadByTool({ toolReference }, { signal: ac.signal })
      .then((response) => {
        if (ac.signal.aborted) return;
        if (response.ok && response.data?.processes) {
          setProcesses(response.data.processes.map((p) => p.process));
        } else if (response.ok) {
          setProcesses([]);
        } else {
          setError(response.message ?? 'errors.generic');
          setProcesses([]);
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        const msg = err instanceof Error ? err.message : 'errors.unknown';
        setError(msg);
        setProcesses([]);
      })
      .finally(() => {
        if (!ac.signal.aborted) setIsLoading(false);
      });

    return () => {
      ac.abort();
    };
  }, [apiClient, toolReference]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const select = useCallback((p: Process) => {
    setSelectedProcess(p);
    setSkipped(false);
  }, []);

  const skip = useCallback(() => {
    setSelectedProcess(null);
    setSkipped(true);
  }, []);

  const reopen = useCallback(() => {
    setSelectedProcess(null);
    setSkipped(false);
  }, []);

  return { processes, isLoading, error, selectedProcess, skipped, select, skip, reopen };
}
